import api from './api';

/**
 * Sync service for cross-client state synchronization.
 * 
 * Polls /auth/sync-state to detect:
 * - Logout from other client (web or extension)
 * - API key changes from other client
 * 
 * FIX (FRONTEND-BUG-001, FRONTEND-BUG-002)
 */
class SyncService {
    constructor() {
        this.interval = null;
        this.pollInterval = 30000; // 30 seconds
        this.lastState = {};
        this.onStateChangeCallback = null;
    }

    /**
     * Start polling for state changes.
     * @param {Function} onStateChange - Callback when state changes
     */
    startPolling(onStateChange) {
        this.onStateChangeCallback = onStateChange;

        // Initial fetch
        this.fetchState();

        // Set up polling interval
        this.interval = setInterval(() => {
            this.fetchState();
        }, this.pollInterval);

        console.log('🔄 Sync polling started (interval: 30s)');
    }

    /**
     * Stop polling.
     */
    stopPolling() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
            console.log('⏹️ Sync polling stopped');
        }
    }

    /**
     * Fetch current state from backend.
     */
    async fetchState() {
        try {
            const response = await api.get('/auth/sync-state');
            const newState = response.data;

            // Compare with last known state
            if (Object.keys(this.lastState).length > 0) {
                const changes = this.detectChanges(newState, this.lastState);
                if (changes.length > 0) {
                    console.log('🔔 State changes detected:', changes);
                    this.onStateChangeCallback?.(newState, this.lastState, changes);
                }
            }

            this.lastState = newState;
        } catch (error) {
            if (error.response?.status === 401) {
                // Token invalidated (blacklisted) by another client
                console.log('⚠️ Token invalidated, triggering logout');
                this.onStateChangeCallback?.(
                    { token_valid: false },
                    this.lastState,
                    ['token_invalidated']
                );
            } else {
                console.error(
                    'Sync poll error:', error.message);
            }
        }
    }

    /**
     * Detect what changed between states.
     */
    detectChanges(newState, oldState) {
        const changes = [];

        if (newState.api_key_configured !== oldState.api_key_configured) {
            changes.push(newState.api_key_configured ? 'api_key_added' : 'api_key_removed');
        }

        if (newState.api_key_updated_at !== oldState.api_key_updated_at) {
            changes.push('api_key_updated');
        }

        return changes;
    }

    /**
     * Set polling interval (for testing/configuration).
     */
    setPollInterval(ms) {
        this.pollInterval = ms;

        // Restart polling if active
        if (this.interval) {
            this.stopPolling();
            this.startPolling(this.onStateChangeCallback);
        }
    }
}

export const syncService = new SyncService();
